/* istanbul ignore file */

import { Module } from 'vuex';

import { AppState } from '@/store';
import { prefixValues } from '@/helpers/util';
import { HeaderOptions, HeaderFlags, FooterOptions, FooterMode } from './header';
import { Routes } from '@/router';
import {
  CustomerDeliveryAddress,
  FormType,
  TokenizedPaymentCard,
  Promotion,
  BasicOrder,
  RecentOrFavoriteOrder,
  Epop,
  OrderWithContext,
  LastSelectedElement,
  Reward,
  Campaign,
  Achievement,
  IngredientMetadata,
  Recipe,
  CreateUserRequest,
  Restaurant,
  RewardWithPromoContext,
  RecentOrFavoriteEntree,
  MenuItem,
  SubmitOrderRequestPaymentMethod,
  Order,
  GroupCampaign,
  Address,
  PurchasedReward
} from '@/models';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import { LoyaltyExplainerModalState } from '@/components/modals/CmgLoyaltyExplainerModal/cmg-loyalty-explainer-modal';
import { countries } from '@/constants';
import { AlternatePickupLocations } from '@/models/alternate-pickup-locations';
import { Milestone, SummerCyoOffer } from '@/models/leaderboard';

export * from './header';

const MUTATION_NAMES = {
  SET_BAG_VISIBILITY: 'setBagVisibility',
  SET_BAG_ICON_VISIBILITY: 'setBagIconVisibility',
  SET_BAG_DATA_ANALYTICS_TYPE: 'setBagDataAnalyticsType',
  REWARD_CONFIRMATION_VISIBILITY: 'setRewardConfirmationVisibility',
  SET_COUNTRY_PROFILE: 'setCountryProfile',
  SET_APP_SCROLLABILITY: 'setAppScrollability',
  SET_MODAL_VISIBILITY: 'setModalVisibility',
  SET_HEADER_OPTIONS: 'setHeaderOptions',
  SET_FOOTER_OPTIONS: 'setFooterOptions',
  INCREMENT_LOADER: 'incrementLoader',
  DECREMENT_LOADER: 'decrementLoader',
  SET_ORDER_REVIEW_TIME: 'setOrderReviewTime',
  SET_ORDER_REVIEW_METHOD: 'setOrderReviewMethod',
  SET_SHOULD_SCROLL_HOME_TO_HASH: 'setShouldScrollHomeToHash',
  SET_CLIENT_WIDTH: 'setClientWidth',
  SET_BANNER_STATE: 'setBannerState',
  SET_CELEBRATION_POINTS_VISIBILITY: 'setCelebrationPointsVisibility',
  SET_ENROLLING_IN_REWARDS: 'setEnrollingInRewards',
  SET_APP_PEPPER_VISIBILITY: 'setPepperVisibility',
  SET_SHOULD_SHOW_DELIVERY_PICK_UP_MODAL: 'setShouldShowDeliveryPickUpModal',
  SET_SESSION_EXPIRED_BANNER: 'setSessionExpiredBanner',
  SET_SHOW_VERIFY_MOBILE_NUMBER: 'setShowVerifyMobileNumber',
  SET_HAS_FAILED_2SV: 'setHasFailed2SV',
  SET_TWO_STEP_VERIFICATION_WELCOME: 'setTwoStepVerificationWelcome',

  // Used for ADA focus tracking
  SET_LAST_SELECTED_ELEMENT: 'setLastSelectedElement',

  // Blurs entire page
  SET_APP_BLURRED: 'setAppBlurred',

  // Blurs content area, which is behind modals and bag
  SET_CONTENT_BLURRED: 'setContentBlurred',

  SET_TOAST_VISIBILITY: 'setToastVisibility',

  SET_CHURROS_TOAST_ALREADY_DISPLAYED: 'setChurrosToastAlreadyDisplayed',
  SET_DIETARY_FILTER_HINT_SHOWN: 'setDietaryFilterHintShown',
  SET_REWARD_TOAST_VISIBILITY: 'setRewardToastVisibility',
  SET_INGREDIENT_PANEL_VISIBILITY: 'setIngredientPanelVisibility',
  SET_RECIPE: 'setRecipe',
  SET_INGREDIENTS: 'setIngredients',
  SET_INGREDIENT: 'setIngredient',
  SET_RECIPE_BANNER_EXPANDED: 'setRecipeBannerExpanded',
  SET_RECIPE_VISIBLE: 'setRecipeVisible',
  SET_RECIPE_DISTANCE: 'setRecipeDistanceLeft',
  SET_UPDATED_MOBILE_NUMBER_ON_LOGIN: 'setUpdatedMobileNumberOnLogin',

  SET_CAMPAIGN_ID: 'setCampaignId',
  SET_CAMPAIGN_CHANNEL_ID: 'setCampaignChannelId',
  SET_REFERRER_CUSTOMER_ID_HASH: 'setReferrerCustomerIdHash',
  SET_SHOULD_BAG_OPEN: 'setShouldBagOpen',
  SET_PCE_LANDING_ROUTE: 'setPCELandingRoute',
  SET_IS_FROM_REWARDS: 'setIsFromRewards',
  SET_MESSAGE_BANNER_STATE: 'setMessageBannerState',

  SET_CHIPOTLE_U_REDIRECT_TO_REWARDS: 'setChipotleURedirectToRewards',
  SET_REFERRED_FROM_CHIPOTLE_U_VERIFICATION_PAGE: 'setReferredFromChipotleUVerificationPage',
  SET_FIRST_TIME_CHIPOTLE_U_VERIFICATION: 'setFirstTimeChipotleUVerification',
};

export const ACTION_NAMES = {
  SET_REWARD_TOAST_VISIBILITY: 'setRewardToastVisibility',
};
export const ACTIONS = prefixValues(ACTION_NAMES, 'ui/');
export const MUTATIONS = prefixValues(MUTATION_NAMES, 'ui/');

// TODO: move the models and constants into the models folder
export enum CountryCode {
  US = 'US',
  CA = 'CA',
  UK = 'GB',
  GB = 'GB',
  FR = 'FR',
  DE = 'DE'
}

export const COUNTRIES_TAX_INCLUSIVE = [CountryCode.UK, CountryCode.GB, CountryCode.FR, CountryCode.DE];

export interface CountryStrings {
  [key: string]: string;
}

export interface CountryProfile {
  countryCode: string;
  name: string;
  lat: number;
  lng: number;
  flagSource?: string;
  currencySymbol: string;
  currencySymbolShort: string;
  topLevelDomain: string;
  strings: CountryStrings;
  locale: string;
  isPepperEnabled: boolean;
  isAdobeAnalyticsEnabled: boolean;
  isAlternatePickupEnabled: boolean;
}

export interface TwoStepVerification {
  email: string;
  phoneNumber: string;
  oldPhoneNumber: string;
  requiresPasswordVerification: boolean;
  requiresSignOutOnFailure: boolean;
  error?: string;
  onSuccess: () => Promise<void>;
  onFailure: () => void;
  onCancel?: () => void;
}

export interface ModalState<TArgs = void> {
  visible: boolean;
  args?: TArgs;
}

export interface Modals {
  rewardsCelebration: ModalState<{
    newRewardCount: number;
  }>;
  rewards: ModalState<{ selectedReward?: Promotion }>;
  rewardsListModal: ModalState<{ rewards?: Promotion[] }>;
  account: ModalState;
  editAccountModal: ModalState;
  editCountryModal: ModalState;
  lostBagModal: ModalState;
  updatePasswordConfirmModal: ModalState<{ isWalletCleared: boolean }>;
  updatePasswordModal: ModalState;
  leaveRewardsModal: ModalState;
  leaveChipotleUModal: ModalState<{
    isLeavingRewards?: boolean;
  }>;
  addCreditOrDebitCardModal: ModalState<{
    oneTimePaymentUse?: boolean;
    providerId?: string;
    onClose?: () => void;
  }>;
  addGiftCardModal: ModalState;
  removePaymentCardModal: ModalState<{
    token: TokenizedPaymentCard;
  }>;
  cancelGroupOrder: ModalState;
  postSubmitCancelOrder: ModalState;
  postSubmitCancelOrderSuccess: ModalState;
  postSubmitCancelOrderFailure: ModalState<{ errorCode: string, }>;
  deliveryAddress: ModalState<{
    isSavedAddress?: boolean;
    deliveryAddress: CustomerDeliveryAddress | null;
    afterClose?: () => void;
  }>;
  repromptCountryProfileModal: ModalState;
  countrySwitchAlertModal: ModalState;
  regionSwitchAlertModal: ModalState;
  signIn: ModalState<{
    /** Route to navigate to after successful sign in */
    route?: Routes | null;
    type?: FormType | null;
    onClose?: () => void;
    prefill?: CreateUserRequest;
  }>;
  verifyMobileNumber: ModalState<TwoStepVerification>;
  twoStepVerificationWelcome: ModalState<TwoStepVerification>;

  /**
   * Note that with the current setup the callback must be called before setModalVisibility( false ) is called
   * or the args will have already been cleared
   */
  findAChipotle: ModalState<{ onClose?: () => void; mode?: string; pickupSearchInput?: string; deliverySearchInput?: string, suppressPickupDeliveryToast?: boolean }>;

  favoriteRestaurant: ModalState<{ restaurantNumber?: number }>;

  mealBuilderMealName: ModalState<{
    beforeClose: (mealName: string) => Promise<any>;
    mealName?: string;
    itemName?: string;
  }>;

  checkoutBadPhoneNumber: ModalState<{ phoneNumber: string | null }>;
  checkoutPaymentIssue: ModalState<{
    order: OrderWithContext;
    isDelivery: boolean;
    onClickSwitchToPayInStore: () => Promise<void>;
    onTryAgain: () => Promise<void>;
    onClose?: () => void;
  }>;

  checkoutAuthorizePaymentCard: ModalState<{
    paymentCard: SubmitOrderRequestPaymentMethod;
    order: Order;
    onApprove: () => Promise<void>;
    onClose?: () => void;
  }>;

  addReward: ModalState;

  achievedBadge: ModalState<{ achievement: Achievement }>;

  celebrateAchievement: ModalState<{ achievement: Achievement }>;

  rewardDetail: ModalState<{
    reward: Reward;
    onRedeem?: () => void | null;
  }>;

  bagOffer: ModalState<{ prevIndex: number }>;

  rewardExtrasExplainer: ModalState;

  summerLeaderboardEducator: ModalState<{ campaign?: Campaign, isActivated?: boolean }>;

  summerLeaderboardMilestoneCompleted: ModalState<{ milestone?: Milestone }>;

  summerLeaderboardCyoReward: ModalState<{ milestone?: Milestone, onBack?: boolean }>;

  summerLeaderboardCyoRedemptionSuccessModal: ModalState<{ offer: SummerCyoOffer; }>;

  chipotleUReverificationSuccessModal: ModalState;
  sweepstakeModal: ModalState<{ title?: string; body?: string }>;

  rewardExtrasCampaignDetail: ModalState<{ campaign: Campaign }>;

  rewardExtrasGroupCampaignDetail: ModalState<{ groupCampaign: GroupCampaign }>;

  rewardRedemptionSuccess: ModalState<RewardWithPromoContext>;

  rewardTermsAndConditions: ModalState<{ termsAndConditions?: string; onBack?: () => void }>;

  rewardsExchangeWelcome: ModalState;

  rewardsSpendYourPoints: ModalState;

  rewardQR: ModalState<{ reward?: Promotion }>;

  basicError: ModalState<{
    header: string | null;
    content: string | null;
    background?: string;
    options: {
      text: string;
      type?: ButtonType;
      onClick: () => void | Promise<any>;
      /** Set this to true inside the onClick event to make the button's loading spinner show */
      isLoading?: boolean;
    }[];
    onClose?: () => void;
    isShort?: boolean;
    unmaskContent?: boolean;
    isInterstitialError?: boolean;
    optionsColumn?: boolean;
  }>;

  personalizedContent: ModalState<{
    showEmailMarketingOptions: boolean;
    onSuccess: () => Promise<void>
    onClose?: (link: string) => Promise<void>;
  }>;

  loyaltyExplainer: ModalState<{ state: LoyaltyExplainerModalState;}>;

  feedback: ModalState;

  retroCreditReceiptModal: ModalState<{ imageUrl: string }>;
  retroCreditConfirmNavModal: ModalState<{ onConfirm: () => void; onCancel: () => void }>;

  fullOrderDetails: ModalState<{ order: BasicOrder; name: string; onClickAddToBag: () => Promise<void> }>;
  addFavoriteOrder: ModalState<{ order: RecentOrFavoriteOrder }>;
  leaveGroupOrder: ModalState;
  noLoyaltyForYou: ModalState;
  toast: ModalState<{
    title?: string;
    subTitle?: string;
    body: string;
    icon?: string;
    bodyOnly?: boolean;
    legalCopy?: string;
    isUpdatedVersion?: boolean;
    visibleTimeLength?: number;
    unmaskContent?: boolean;
    after?: () => void;
  }>;
  bagReconciliation: ModalState<{
    restaurantId: number;
    isPickup: boolean;
    itemsIds: string[];
    onCancel: () => void;
    onRemoveItems: () => void;
  }>;
  epop: ModalState<{
    epops: Epop[];
  }>;
  globalErrorToast: ModalState;
  giftCardQr: ModalState;
  deliveryOfflineModal: ModalState<{
    onSwitchToPickup: () => Promise<void>;
    onClose?: () => void;
  }>;
  dietaryFiltersModal: ModalState;
  rewardsCountryMismatch: ModalState;
  subscribeEmailMarketing: ModalState;
  mobileNutritionDetails: ModalState;
  guacModeVerified: ModalState;
  guacModeJoinRewards: ModalState;
  guacModeSignIn: ModalState<{
    type?: FormType | null;
  }>;
  rewardsMembersOnlyModal: ModalState;
  twoStepVerificationLearnMoreModal: ModalState;
  exitMobileVerificationModal: ModalState<{
    onLeave: () => void;
  }>;
  verificationSystemErrorModal: ModalState<{
    requiresSignOutOnFailure?: boolean;
    onLeave: () => Promise<void>;
  }>;
  clearWalletModal: ModalState;
  lockedOutModal: ModalState;
  pickupOptionsModal: ModalState<{ restaurant: Restaurant }>;
  maxEntreeLimitModal: ModalState<{
    errorMessage: string;
    errorCode: string;
  }>;
  oneTapReorderDetailsModal: ModalState<{
    orderDate: string;
    entree: RecentOrFavoriteEntree | undefined;
    onAddToBag: () => void;
  }>;
  oneTapReorderUnavailabilityModal: ModalState<{
    entree: RecentOrFavoriteEntree;
    unavailableItems: MenuItem[];
    onAddToBag: (updatedEntree: RecentOrFavoriteEntree) => void;
  }>;
  alternatePickupLocationsModal: ModalState<{ 
    alternatePickUpLocations: AlternatePickupLocations;
    onAlternateLocationSelected: (restaurant: Restaurant) => void;
  }>;
  orderReviewModal: ModalState<{
    isPickupOrder?: boolean;
    restaurant: Restaurant | null;
    countdownTimerValue: number,
    address?: Address | null;
  }>;
  idMeVerificationModal: ModalState;
  chipotleUJoinRewardsPromptModal: ModalState<{
    onJoinRewards: () => Promise<void>;
    onContinueWithoutJoining: () => Promise<void>;
  }>;
  studentRegistrationSuccessModal: ModalState;
  scanlessIntroModal: ModalState;
  scanlessInfoIconModal: ModalState;
  cyobRewardModal: ModalState<{
    showLaterOption: boolean;
  }>;
  cyobRedemptionSuccessModal: ModalState<{
    purchasedReward: PurchasedReward;
    reward: Reward;
  }>;
  tryAgainModal: ModalState<{
    onClickTryAgain: () => Promise<void>;
  }>;
  referAFriendModal: ModalState;
  referralJoinSuccessModal: ModalState;
  alreadyAMemberModal: ModalState;
  youreInvitedModal: ModalState;
  referralNotEligibleModal: ModalState;
  soeRankDescriptionModal: ModalState<{ type: 'local' | 'state' | 'national' }>;
  autoAccrualErrorModal: ModalState<{
    header?: string;
    content?: string;
    options: {
      text: string;
      type?: ButtonType;
      onClick: () => void | Promise<any>;
      /** Set this to true inside the onClick event to make the button's loading spinner show */
      isLoading?: boolean;
    }[];
  }>;
  employeeRewardsHowItWorksModal: ModalState;
}

/**
 * Note that with the current setup the callback must be called before setModalVisibility( false ) is called
 * or the args will have already been cleared
 */
export interface FindAChipotleModalArgs {
  onClose: () => void;
}

export interface SetModalVisibilityArgs<TModal extends keyof Modals> {
  modal: TModal;
  state: {
    visible: boolean;
    args: Modals[TModal]['args'] | null;
  };
}

// Track number of things loading so a parent object can show/hide
// a loader without needing events from children
export interface Loaders {
  bag: number;
  pickupTimes: number;
}

export interface BannerState {
  visible: boolean;
  message: string;
  details?: string;
  backgroundColor?: string;
  timeoutMs?: number;
}

export interface UiModuleState {
  bag: {
    visible: boolean;
    iconVisible: boolean;
    rewardConfirmation: boolean;
    bagToastVisibility: boolean;
    dataAnalyticsType: string;
  };

  ingredientsPage: {
    ingredientsPanelVisibility: boolean;
    selectedRecipe: Recipe | null;
    visibleIngredients: IngredientMetadata[];
    selectedIngredient: IngredientMetadata | null;
    isRecipeBannerExpanded: boolean;
    selectedRecipeDistance: number;
    isSelectedRecipeVisible: boolean;
  };

  app: {
    scrollable: boolean;
    blurred: boolean;
    contentBlurred: boolean;
    pepperVisibility: boolean;
  };

  countryProfile: CountryProfile;
  countryInitialized: boolean;
  modals: Modals;
  loaders: Loaders;

  header: {
    options: HeaderOptions;
  };

  footer: {
    options: FooterOptions;
  };

  home: {
    shouldScrollToHash: boolean;
  };

  orderConfirmation: {
    hasFailed2SV: boolean;
  };

  orderCancellation: {
    isOrderCancellationEnabled: boolean;
    orderReviewTime: string;
    orderConfirmationMethod: string;
  };


  clientWidth: number;
  notificationBanner: BannerState;
  pointsCelebrationVisible: boolean;
  enrollingInRewards: boolean;
  shouldShowDeliveryPickUpModal: boolean;
  lastSelectedElement: LastSelectedElement | null;
  toastVisibility: boolean;
  churrosToastAlreadyDisplayed: boolean;
  dietaryFilterHintShown: boolean;
  showSessionExpiredBanner: boolean;
  showVerifyMobileNumber: boolean;
  selectedUpdateMobileNumberOnLogin: boolean;
  campaignId?: string;
  campaignChannelId?: string;
  referrerCustomerIdHash?: string;
  shouldBagOpen?: boolean;
  pceLandingRoute?: string;
  isFromRewards?: boolean;
  messageBanner: BannerState;
  chipotleURedirectToRewards: boolean;
  referredFromChipotleUVerificationPage: boolean;
  firstTimeChipotleUVerification: boolean;
}

const DEFAULT_STATE: UiModuleState = {
  bag: {
    visible: false,
    iconVisible: true,
    rewardConfirmation: false,
    bagToastVisibility: false,
    dataAnalyticsType: 'section',
  },
  ingredientsPage: {
    ingredientsPanelVisibility: false,
    selectedRecipe: null,
    visibleIngredients: [],
    selectedIngredient: null,
    isRecipeBannerExpanded: false,
    isSelectedRecipeVisible: false,
    selectedRecipeDistance: 0
  },
  app: {
    scrollable: true,
    blurred: false,
    contentBlurred: false,
    pepperVisibility: false,
  },
  countryInitialized: false,
  countryProfile: {
    name: countries.us.name,
    lat: countries.us.lat,
    lng: countries.us.lng,
    countryCode: countries.us.countryCode,
    currencySymbol: countries.us.currencySymbol,
    currencySymbolShort: countries.us.currencySymbolShort,
    topLevelDomain: countries.us.topLevelDomain,
    strings: countries.us.strings,
    locale: countries.us.locale,
    isPepperEnabled: countries.us.isPepperEnabled,
    isAdobeAnalyticsEnabled: true,
    isAlternatePickupEnabled: countries.us.isAdobeAnalyticsEnabled
  },
  modals: {
    signIn: { visible: false },
    rewardsListModal: { visible: false },
    verifyMobileNumber: { visible: false },
    twoStepVerificationWelcome: { visible: false },
    rewards: { visible: false },
    account: { visible: false },
    countrySwitchAlertModal: { visible: false },
    regionSwitchAlertModal: { visible: false },
    editAccountModal: { visible: false },
    editCountryModal: { visible: false },
    lostBagModal: { visible: false },
    updatePasswordConfirmModal: { visible: false },
    updatePasswordModal: { visible: false },
    leaveRewardsModal: { visible: false },
    leaveChipotleUModal: { visible: false },
    findAChipotle: { visible: false },
    favoriteRestaurant: { visible: false },
    addCreditOrDebitCardModal: { visible: false },
    addGiftCardModal: { visible: false },
    removePaymentCardModal: { visible: false },
    cancelGroupOrder: { visible: false },
    postSubmitCancelOrder: { visible: false },
    postSubmitCancelOrderSuccess: { visible: false },
    postSubmitCancelOrderFailure: { visible: false, },
    deliveryAddress: { visible: false },
    mealBuilderMealName: { visible: false },
    basicError: { visible: false },
    checkoutBadPhoneNumber: { visible: false },
    checkoutPaymentIssue: { visible: false },
    checkoutAuthorizePaymentCard: { visible: false },
    addReward: { visible: false },
    achievedBadge: { visible: false },
    celebrateAchievement: { visible: false },
    rewardDetail: { visible: false },
    bagOffer: { visible: false },
    rewardExtrasExplainer: { visible: false },
    rewardExtrasCampaignDetail: { visible: false },
    rewardExtrasGroupCampaignDetail: { visible: false },
    rewardRedemptionSuccess: { visible: false },
    rewardTermsAndConditions: { visible: false },
    rewardsExchangeWelcome: { visible: false },
    rewardsSpendYourPoints: { visible: false },
    rewardQR: { visible: false },
    loyaltyExplainer: { visible: false },
    feedback: { visible: false },
    repromptCountryProfileModal: { visible: false },
    retroCreditReceiptModal: { visible: false },
    retroCreditConfirmNavModal: { visible: false },
    fullOrderDetails: { visible: false },
    addFavoriteOrder: { visible: false },
    leaveGroupOrder: { visible: false },
    rewardsCelebration: { visible: false },
    noLoyaltyForYou: { visible: false },
    toast: { visible: false },
    bagReconciliation: { visible: false },
    epop: { visible: false },
    globalErrorToast: { visible: false },
    giftCardQr: { visible: false },
    deliveryOfflineModal: { visible: false },
    dietaryFiltersModal: { visible: false },
    rewardsCountryMismatch: { visible: false },
    subscribeEmailMarketing: { visible: false },
    personalizedContent: { visible: false },
    mobileNutritionDetails: { visible: false },
    guacModeVerified: { visible: false },
    guacModeJoinRewards: { visible: false },
    guacModeSignIn: { visible: false },
    rewardsMembersOnlyModal: { visible: false },
    twoStepVerificationLearnMoreModal: { visible: false },
    exitMobileVerificationModal: { visible: false },
    verificationSystemErrorModal: { visible: false },
    clearWalletModal: { visible: false },
    lockedOutModal: { visible: false },
    pickupOptionsModal: { visible: false },
    maxEntreeLimitModal: { visible: false },
    oneTapReorderDetailsModal: { visible: false },
    oneTapReorderUnavailabilityModal: { visible: false },
    summerLeaderboardEducator: { visible: false },
    summerLeaderboardMilestoneCompleted: { visible: false },
    summerLeaderboardCyoReward: { visible: false },
    summerLeaderboardCyoRedemptionSuccessModal: { visible: false },
    sweepstakeModal: { visible: false },
    alternatePickupLocationsModal: { visible: false },
    orderReviewModal: { visible: false },
    idMeVerificationModal: { visible: false },
    chipotleUJoinRewardsPromptModal: { visible: false },
    studentRegistrationSuccessModal: { visible: false },
    scanlessIntroModal: { visible: false },
    scanlessInfoIconModal: { visible: false },
    cyobRewardModal: { visible: false },
    cyobRedemptionSuccessModal: { visible: false },
    tryAgainModal: { visible: false },
    referAFriendModal: { visible: false },
    referralJoinSuccessModal: { visible: false },
    alreadyAMemberModal: { visible: false },
    youreInvitedModal: { visible: false },
    referralNotEligibleModal: { visible: false },
    soeRankDescriptionModal: { visible: false },
    autoAccrualErrorModal: { visible: false },
    chipotleUReverificationSuccessModal: { visible: false },
    employeeRewardsHowItWorksModal: { visible: false },
  },
  loaders: {
    bag: 0,
    pickupTimes: 0,
  },
  header: {
    options: { flags: HeaderFlags.All & ~HeaderFlags.ReturnToRoute & ~HeaderFlags.LogoVisibleXS },
  },
  footer: {
    options: { mode: FooterMode.Normal },
  },
  home: {
    shouldScrollToHash: false,
  },
  orderConfirmation: {
    hasFailed2SV: false,
  },
  orderCancellation: {
    isOrderCancellationEnabled: false,
    orderReviewTime: '0',
    orderConfirmationMethod: '',
  },
  clientWidth: 0,
  notificationBanner: { visible: false, message: '' },
  pointsCelebrationVisible: false,
  enrollingInRewards: false,
  shouldShowDeliveryPickUpModal: false,
  lastSelectedElement: null,
  toastVisibility: false,
  churrosToastAlreadyDisplayed: false,
  dietaryFilterHintShown: false,

  showSessionExpiredBanner: false,
  showVerifyMobileNumber: false,
  selectedUpdateMobileNumberOnLogin: false,
  campaignId: '',
  campaignChannelId: '',
  referrerCustomerIdHash: '',

  shouldBagOpen: false,
  pceLandingRoute: '',

  isFromRewards: false,
  messageBanner: { visible: false, message: '' },

  chipotleURedirectToRewards: false,
  referredFromChipotleUVerificationPage: false,
  firstTimeChipotleUVerification: false,
};

export const ALL_MODAL_STRINGS = Object.keys(DEFAULT_STATE.modals);

export function makeUiModule(): Module<UiModuleState, AppState> {
  return {
    namespaced: true,
    state: DEFAULT_STATE,
    actions: {
      [ACTION_NAMES.SET_REWARD_TOAST_VISIBILITY]: ({ commit, state, getters }) => {
        commit(MUTATION_NAMES.SET_REWARD_TOAST_VISIBILITY, true);
        setTimeout(() => {
          commit(MUTATION_NAMES.SET_REWARD_TOAST_VISIBILITY, false);
        }, 8000);

      },
    },
    mutations: {
      [MUTATION_NAMES.SET_REWARD_TOAST_VISIBILITY]: (state, visible: boolean) => {
        state.bag.bagToastVisibility = visible;
      },
      [MUTATION_NAMES.SET_BAG_VISIBILITY]: (state, visible) => {
        state.bag.visible = visible;
      },
      [MUTATION_NAMES.SET_BAG_ICON_VISIBILITY]: (state, visible) => {
        state.bag.iconVisible = visible;
      },
      [MUTATION_NAMES.SET_BAG_DATA_ANALYTICS_TYPE]: (state, dataAnalyticsType) => {
        state.bag.dataAnalyticsType = dataAnalyticsType;
      },
      [MUTATION_NAMES.REWARD_CONFIRMATION_VISIBILITY]: (state, visible) => {
        state.bag.rewardConfirmation = visible;
        setTimeout(() => {
          state.bag.rewardConfirmation = false;
        }, 3000);
      },
      [MUTATION_NAMES.SET_APP_SCROLLABILITY]: (state, scrollable) => {
        state.app.scrollable = scrollable;
      },
      [MUTATION_NAMES.SET_APP_BLURRED]: (state, blurred) => {
        state.app.blurred = blurred;
      },
      [MUTATION_NAMES.SET_COUNTRY_PROFILE]: (state, countryProfile) => {
        state.countryProfile = countryProfile;
        state.countryInitialized = true;
      },
      [MUTATION_NAMES.SET_CONTENT_BLURRED]: (state, blurred) => {
        state.app.contentBlurred = blurred;
      },
      [MUTATION_NAMES.SET_MODAL_VISIBILITY]: (state, args: SetModalVisibilityArgs<any>) => {
        (state.modals as any)[args.modal] = args.state;
      },
      [MUTATION_NAMES.SET_HEADER_OPTIONS]: (state, headerOptions: HeaderOptions) => {
        state.header.options = headerOptions;
      },
      [MUTATION_NAMES.SET_FOOTER_OPTIONS]: (state, options: FooterOptions) => {
        state.footer.options = options;
      },
      [MUTATION_NAMES.SET_ORDER_REVIEW_TIME]: (state, value) => {
        state.orderCancellation.orderReviewTime = value;
      },
      [MUTATION_NAMES.SET_ORDER_REVIEW_METHOD]: (state, value) => {
        state.orderCancellation.orderConfirmationMethod = value;
      },
      [MUTATION_NAMES.INCREMENT_LOADER]: (state, loader: keyof Loaders) => {
        state.loaders[loader]++;
      },
      [MUTATION_NAMES.DECREMENT_LOADER]: (state, loader: keyof Loaders) => {
        state.loaders[loader]--;
      },
      [MUTATION_NAMES.SET_SHOULD_SCROLL_HOME_TO_HASH]: (state, value: boolean) => {
        state.home.shouldScrollToHash = value;
      },
      [MUTATION_NAMES.SET_HAS_FAILED_2SV]: (state, value: boolean) => {
        state.orderConfirmation.hasFailed2SV = value;
      },
      [MUTATION_NAMES.SET_CLIENT_WIDTH]: (state, width: number) => {
        state.clientWidth = width;
      },
      [MUTATION_NAMES.SET_BANNER_STATE]: (state, bannerState: BannerState) => {
        state.notificationBanner = bannerState;
      },
      [MUTATION_NAMES.SET_CELEBRATION_POINTS_VISIBILITY]: (state, isVisible: boolean) => {
        state.pointsCelebrationVisible = isVisible;
      },
      [MUTATION_NAMES.SET_ENROLLING_IN_REWARDS]: (state, enrolling: boolean) => {
        state.enrollingInRewards = enrolling;
      },
      [MUTATION_NAMES.SET_APP_PEPPER_VISIBILITY]: (state, visible: boolean) => {
        state.app.pepperVisibility = visible;
      },
      [MUTATION_NAMES.SET_SHOULD_SHOW_DELIVERY_PICK_UP_MODAL]: (state, shouldShow: boolean) => {
        state.shouldShowDeliveryPickUpModal = shouldShow;
      },
      [MUTATION_NAMES.SET_LAST_SELECTED_ELEMENT]: (state, lastSelectedElement: LastSelectedElement) => {
        state.lastSelectedElement = lastSelectedElement;
      },
      [MUTATION_NAMES.SET_TOAST_VISIBILITY]: (state, visible: boolean) => {
        state.toastVisibility = visible;
      },
      [MUTATION_NAMES.SET_CHURROS_TOAST_ALREADY_DISPLAYED]: (state, displayed: boolean) => {
        state.churrosToastAlreadyDisplayed = displayed;
      },
      [MUTATION_NAMES.SET_DIETARY_FILTER_HINT_SHOWN]: (state, dietaryFilterHintShown: boolean) => {
        state.dietaryFilterHintShown = dietaryFilterHintShown;
      },
      [MUTATION_NAMES.SET_RECIPE]: (state, recipe: Recipe) => {
        state.ingredientsPage.selectedRecipe = recipe;
      },
      [MUTATION_NAMES.SET_INGREDIENT_PANEL_VISIBILITY]: (state, visible: boolean) => {
        state.ingredientsPage.ingredientsPanelVisibility = visible;
      },
      [MUTATION_NAMES.SET_INGREDIENTS]: (state, ingredients: IngredientMetadata[]) => {
        state.ingredientsPage.visibleIngredients = ingredients;
      },
      [MUTATION_NAMES.SET_INGREDIENT]: (state, ingredient: IngredientMetadata) => {
        state.ingredientsPage.selectedIngredient = ingredient;
      },
      [MUTATION_NAMES.SET_RECIPE_BANNER_EXPANDED]: (state, isExpanded: boolean) => {
        state.ingredientsPage.isRecipeBannerExpanded = isExpanded;
      },
      [MUTATION_NAMES.SET_RECIPE_VISIBLE]: (state, isVisible: boolean) => {
        state.ingredientsPage.isSelectedRecipeVisible = isVisible;
      },
      [MUTATION_NAMES.SET_RECIPE_DISTANCE]: (state, distance: number) => {
        state.ingredientsPage.selectedRecipeDistance = distance;
      },
      [MUTATION_NAMES.SET_SESSION_EXPIRED_BANNER]: (state, showSessionExpiredBanner: boolean) => {
        state.showSessionExpiredBanner = showSessionExpiredBanner;
      },
      [MUTATION_NAMES.SET_SHOW_VERIFY_MOBILE_NUMBER]: (state, showVerifyMobileNumber: boolean) => {
        state.showVerifyMobileNumber = showVerifyMobileNumber;
      },
      [MUTATION_NAMES.SET_UPDATED_MOBILE_NUMBER_ON_LOGIN]: (state, selectedUpdateMobileNumber: boolean) => {
        state.selectedUpdateMobileNumberOnLogin = selectedUpdateMobileNumber;
      },
      [MUTATION_NAMES.SET_CAMPAIGN_ID]: (state, campaignId: string) => {
        state.campaignId = campaignId;
      },
      [MUTATION_NAMES.SET_CAMPAIGN_CHANNEL_ID]: (state, campaignChannelId: string) => {
        state.campaignChannelId = campaignChannelId;
      },
      [MUTATION_NAMES.SET_REFERRER_CUSTOMER_ID_HASH]: (state, referrerCustomerIdHash: string) => {
        state.referrerCustomerIdHash = referrerCustomerIdHash;
      },
      [MUTATION_NAMES.SET_SHOULD_BAG_OPEN]: (state, shouldBagOpen: boolean) => {
        state.shouldBagOpen = shouldBagOpen;
      },
      [MUTATION_NAMES.SET_PCE_LANDING_ROUTE]: (state, pceLandingRoute: string) => {
        state.pceLandingRoute = pceLandingRoute;
      },
      [MUTATION_NAMES.SET_IS_FROM_REWARDS]: (state, isFromRewards: boolean) => {
        state.isFromRewards = isFromRewards;
      },
      [MUTATION_NAMES.SET_MESSAGE_BANNER_STATE]: (state, messageBannerState: BannerState) => {
        state.messageBanner = messageBannerState;
      },
      [MUTATION_NAMES.SET_CHIPOTLE_U_REDIRECT_TO_REWARDS]: (state, value: boolean) => {
        state.chipotleURedirectToRewards = value;
      },
      [MUTATION_NAMES.SET_REFERRED_FROM_CHIPOTLE_U_VERIFICATION_PAGE]: (state, value: boolean) => {
        state.referredFromChipotleUVerificationPage = value;
      },
      [MUTATION_NAMES.SET_FIRST_TIME_CHIPOTLE_U_VERIFICATION]: (state, value: boolean) => {
        state.firstTimeChipotleUVerification = value;
      },
    },
  };
}
